import { Select } from '@any-ui-react/core';
import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { ManagerialAccountingQuarter } from '@/common/graphql';
import { QUARTER_OPTIONS, QUARTERS_IN_ORDER } from '@/common/types';
import { DateUtils } from '@/common/utils';

import { YearFilter } from '../year-half-filter/YearFilter';

const ANYMIND_START_YEAR = DateUtils.dateAnyMindStarts.getFullYear();

// Derives the quarter's first/last calendar month from its position in QUARTERS_IN_ORDER
// (Q1=months 01-03, Q2=04-06, etc.) instead of a hand-typed per-quarter table.
const quarterStartMonth = (quarter: ManagerialAccountingQuarter): string =>
  String(QUARTERS_IN_ORDER.indexOf(quarter) * 3 + 1).padStart(2, '0');

const quarterEndMonth = (quarter: ManagerialAccountingQuarter): string =>
  String(QUARTERS_IN_ORDER.indexOf(quarter) * 3 + 3).padStart(2, '0');

// Unlike a half-year, a quarter's own natural bounds are never clamped to AnyMind's 2016-04-01
// data start: Q1 of the start year (Jan-Mar) never overlaps that date at all, so clamping it
// forward would misrepresent a later quarter's real data as Q1's. The quarter picker below
// instead prevents Q1 from being selected for the start year in the first place.
export const yearQuarterToStartYearMonth = (year: number, quarter: ManagerialAccountingQuarter): string =>
  `${year}-${quarterStartMonth(quarter)}`;

export const yearQuarterToEndYearMonth = (year: number, quarter: ManagerialAccountingQuarter): string =>
  `${year}-${quarterEndMonth(quarter)}`;

// Q1 of the start year has no data at all (data starts April 2016, entirely after Q1's own
// Jan-Mar bounds), so it is excluded from the quarter options for that year.
const quarterOptionsForYear = (year: string): typeof QUARTER_OPTIONS =>
  Number(year) === ANYMIND_START_YEAR ? QUARTER_OPTIONS.slice(1) : QUARTER_OPTIONS;

const isInvalidStartYearQ1 = (year: string, quarter: ManagerialAccountingQuarter): boolean =>
  Number(year) === ANYMIND_START_YEAR && quarter === ManagerialAccountingQuarter.Q1;

export interface YearQuarterRangeValue {
  fromYear: string;
  fromQuarter: ManagerialAccountingQuarter;
  toYear: string;
  toQuarter: ManagerialAccountingQuarter;
}

interface YearQuarterRangeSelectProps {
  className?: string;
  disabled?: boolean;
  value: YearQuarterRangeValue;
  onChange: (value: YearQuarterRangeValue) => void;
}

export const YearQuarterRangeSelect = ({ className, disabled, value, onChange }: YearQuarterRangeSelectProps) => {
  const fromQuarterOptions = useMemo(() => quarterOptionsForYear(value.fromYear), [value.fromYear]);
  const toQuarterOptions = useMemo(() => quarterOptionsForYear(value.toYear), [value.toYear]);

  const isFromInvalid = isInvalidStartYearQ1(value.fromYear, value.fromQuarter);
  const isToInvalid = isInvalidStartYearQ1(value.toYear, value.toQuarter);
  // Resolved synchronously so the Selects below are never rendered with a `value` that's
  // missing from their own `data` list (Q1 is excluded from quarterOptionsForYear above).
  const validFromQuarter = isFromInvalid ? ManagerialAccountingQuarter.Q2 : value.fromQuarter;
  const validToQuarter = isToInvalid ? ManagerialAccountingQuarter.Q2 : value.toQuarter;

  // Requests the owning state be updated to match the resolved values above, so the correction
  // persists past this render. From and To are corrected together in a single onChange call —
  // firing two separate onChange calls (one per side) here would have each spread the same
  // pre-correction `value` snapshot, so the second call's spread would silently revert the
  // first call's fix for one render before self-healing.
  useEffect(() => {
    if (isFromInvalid || isToInvalid) {
      onChange({ ...value, fromQuarter: validFromQuarter, toQuarter: validToQuarter });
    }
  }, [isFromInvalid, isToInvalid, validFromQuarter, validToQuarter, onChange, value]);

  return (
    <div className={classNames('flex flex-wrap items-center gap-2', className)}>
      <YearFilter
        disabled={disabled}
        placeholder="From Year"
        value={value.fromYear}
        onChange={y => onChange({ ...value, fromYear: y })}
      />
      <Select
        className="w-36"
        data={fromQuarterOptions}
        disabled={disabled}
        placeholder="Quarter"
        value={validFromQuarter}
        onChange={v =>
          onChange({ ...value, fromQuarter: (v as ManagerialAccountingQuarter) ?? ManagerialAccountingQuarter.Q1 })
        }
      />
      <span aria-hidden="true">–</span>
      <YearFilter
        disabled={disabled}
        placeholder="To Year"
        value={value.toYear}
        onChange={y => onChange({ ...value, toYear: y })}
      />
      <Select
        className="w-36"
        data={toQuarterOptions}
        disabled={disabled}
        placeholder="Quarter"
        value={validToQuarter}
        onChange={v =>
          onChange({ ...value, toQuarter: (v as ManagerialAccountingQuarter) ?? ManagerialAccountingQuarter.Q1 })
        }
      />
    </div>
  );
};
